import { useState, forwardRef, useImperativeHandle } from 'react';
import { IToolData } from 'types/products';
import { NudosButton, NudosTextArea } from 'components/NudosComponents';
import { updateToolCondition } from 'services/toolsModule';
import { displayErrorNotification, displaySuccessNotification } from 'utils/displayNudosStandardNotifications';
import BordRadioSelectDropdown, {
  IBordRadioSelectOption
} from 'components/NudosComponents/BordRadioSelectDropdown/BordRadioSelectDropdown';
import NudosConfirmActionModal from 'components/NudosComponents/NudosConfirmActionModal/NudosConfirmActionModal';
import './ProductEditConditional.scss';

const ProductEditConditional = forwardRef<{ hasUnsavedChanges: () => boolean }, IProductEditConditionalProps>(
  ({ productData, onClose, setRefreshProductData, refreshToolsListing }, ref) => {
  const { availableConditions, condition } = productData;
  const { id: conditionId, comments } = condition ?? {};
  const [selectedCondition, setSelectedCondition] = useState<string>(conditionId?.toString() ?? '');
  const [reasonForModificationLocal, setReasonForModificationLocal] = useState<string>(comments ?? '');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
  const conditionOptions: IBordRadioSelectOption[] =
    availableConditions?.map(condition => ({
      id: condition.id.toString(),
      label: condition.name ?? '',
      value: condition.id.toString(),
      description: condition.description ?? ''
    })) ?? [];


  const bodyToUpdate = {
    productId: Number(productData.productId),
    toolConditionId: Number(selectedCondition),
    comments: reasonForModificationLocal
  };

  const hasCommentsChanged = comments !== reasonForModificationLocal && reasonForModificationLocal?.length > 0;
  const disableButtonSave = isLoading || !selectedCondition || !hasCommentsChanged;

  const hasUnsavedChanges = () => {
    const hasConditionChanged = selectedCondition !== conditionId?.toString();
    const hasCommentsChanged = reasonForModificationLocal !== (comments ?? '');
    return hasConditionChanged || hasCommentsChanged;
  };

  useImperativeHandle(ref, () => ({
    hasUnsavedChanges
  }));
  const handleCancel = () => {
    if(!hasUnsavedChanges()) return onClose(true);
    setShowConfirmationModal(true);
  };

  const handleExitWithoutSaving = () => {
    if (conditionId) {
      setSelectedCondition(conditionId.toString());
    }
    setReasonForModificationLocal(comments ?? '');
    setShowConfirmationModal(false);
    onClose(true);
  };

  const handleContinueEditing = () => {
    setShowConfirmationModal(false);
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await updateToolCondition(bodyToUpdate);
      displaySuccessNotification({ customJSXMessage: <>La condición de la herramienta se ha modificado exitosamente</> });
      setRefreshProductData(true);
      refreshToolsListing?.();
      onClose(true);
    } catch (error) {
      displayErrorNotification();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="productEditConditional">
      <BordRadioSelectDropdown
        label="Condición"
        options={conditionOptions}
        selectedValue={selectedCondition}
        onValueChange={setSelectedCondition}
        componentSize="fullWidth"
        placeholder="Selecciona una condición"
        isOptionsContainerFullHeight
        customClassName="bordRadioSelectDropdownConditionOptions"
        customClassNameOptionsContainer="conditionOptionsContainerCustom"
        customClassNameSelectOption="conditionSelectOption"
      />
      <NudosTextArea
        label="Razón de la modificación"
        placeholder="Escribe aquí las razones..."
        currentValue={reasonForModificationLocal}
        setCurrentValue={setReasonForModificationLocal}
        customClassName="reasonForModificationTextAreaConditional"
        customTextAreaClassName="reasonForModificationTextAreaTextAreaConditional"
        customLabelClassName="reasonForModificationTextAreaLabelConditional"
        maxLength={1000}
        noResize
      />

      <div className="containerButtons">
        <NudosButton
          buttonText="Cancelar modificación"
          componentSize="small"
          handleClick={handleCancel}
          colorPalette="secondary"
          isButtonLoading={isLoading}
          isButtonDisabled={isLoading}
        />
        <NudosButton
          buttonText="Modificar condición"
          handleClick={handleSave}
          isButtonLoading={isLoading}
          isButtonDisabled={disableButtonSave}
        />
      </div>

      {showConfirmationModal && (
        <NudosConfirmActionModal
          modalTitle="Tienes cambios sin guardar"
          modalText="Si sales, se perderá la información ingresada. ¿Seguro que quieres cancelar modificación de la condición?"
          firstButtonText="Salir sin guardar"
          secondButtonText="Seguir editando"
          firstButtonAction={handleExitWithoutSaving}
          secondButtonAction={handleContinueEditing}
          customHeight="260px"
          customStyleModalTitle={
            { marginBottom: '5px' }
          }
        />
      )}
    </div>
  );
  }
);

export default ProductEditConditional;

interface IProductEditConditionalProps {
  productData: IToolData;
  onClose: (skipValidation?: boolean) => void;
  setRefreshProductData: React.Dispatch<React.SetStateAction<boolean>>;
  refreshToolsListing?: () => void;
}
